import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Archive,
  Calendar,
  Calculator,
  FilePlus2,
  FileText,
  ExternalLink,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  Pencil,
  ReceiptText,
  RefreshCw,
  Save,
  Ship,
  Sparkles,
  Trash2,
  Truck,
  Upload,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api/client'
import { formatCurrency } from '@/lib/utils'
import { ErrorState } from '@/src/components/ErrorState'
import { LoadingState } from '@/src/components/LoadingState'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import type {
  Consignment,
  ConsignmentDocument,
  ConsignmentDocumentType,
  ConsignmentDetail,
  ConsignmentItem,
  HelmCommunication,
  HelmCommunicationChannel,
  HelmInquiryStage,
  HelmServiceType,
  SeaTrackingContainer,
  SeaTrackingShipment,
  UpdateConsignmentRequest,
} from '@/lib/api/types'
import { getConsignmentTrackingReference, shouldLoadSeaTracking } from '@/src/lib/tracking'

const statusOptions: Array<NonNullable<UpdateConsignmentRequest['status']>> = [
  'DRAFT',
  'AWAITING_INVOICE_FROM_FORWARDER',
  'AWAITING_PAYMENT',
  'PAYMENT_CONFIRMED',
  'IN_TRANSIT_DOMESTIC',
  'IN_TRANSIT_INTERNATIONAL',
  'ARRIVED',
  'CLEARING',
  'READY_FOR_PICKUP',
  'DELIVERED',
  'CLOSED',
]

const serviceTypeOptions: HelmServiceType[] = [
  'PRODUCT_SOURCING',
  'SHIPPING_ONLY',
  'PVOC_TESTING',
  'CUSTOMS_CLEARANCE',
  'END_TO_END_IMPORT',
]

const inquiryStageOptions: HelmInquiryStage[] = [
  'NEW_INQUIRY',
  'QUALIFYING',
  'QUOTED',
  'AWAITING_CUSTOMER',
  'ACTIVE',
  'BLOCKED',
  'COMPLETED',
  'CLOSED',
]

const communicationChannelOptions: HelmCommunicationChannel[] = ['EMAIL', 'WHATSAPP', 'BOTH', 'NONE']

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function displayTracking(consignment: Consignment) {
  if (consignment.international_tracking_number) return consignment.international_tracking_number
  if (consignment.tracking_number) return consignment.tracking_number
  if (consignment.domestic_tracking_numbers?.length) return consignment.domestic_tracking_numbers.join(', ')
  return 'Not assigned'
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleDateString()
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not set'
  return new Date(value).toLocaleString()
}

function normalizeWhatsAppPhone(phone: string | null | undefined) {
  return phone?.replace(/\D/g, '') || ''
}

function statusVariant(status: string | null | undefined) {
  if (status === 'DELIVERED' || status === 'CLOSED' || status === 'PAYMENT_CONFIRMED') return 'secondary' as const
  if (status === 'AWAITING_PAYMENT' || status === 'CLEARING') return 'outline' as const
  return 'default' as const
}

function readString(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  return typeof entry === 'string' && entry.trim() ? entry.trim() : null
}

function readNumber(value: Record<string, unknown> | null | undefined, key: string) {
  const entry = value?.[key]
  return typeof entry === 'number' && Number.isFinite(entry) ? entry : null
}

function describeContainerMovement(container: SeaTrackingContainer | undefined) {
  if (!container) return 'No container movement has been returned yet.'
  const movement = container.last_movement
  const event = readString(movement, 'event') || readString(movement, 'status') || readString(movement, 'description')
  const location = readString(movement, 'location') || container.pod_terminal_name || readString(container.pod, 'name')
  const vessel = [container.arriving_vessel_name, container.arriving_vessel_voyage_no].filter(Boolean).join(' ')

  return [
    event ? formatLabel(event) : null,
    location ? `at ${location}` : null,
    vessel ? `via ${vessel}` : null,
  ].filter(Boolean).join(' ') || 'Container movement is available in Visiwise.'
}

function getShipmentContainerCount(shipment: SeaTrackingShipment | null | undefined, containers: SeaTrackingContainer[]) {
  return readNumber(shipment?.tracking || null, 'total_containers_count') ?? containers.length
}

function TaxAmountRow({
  label,
  value,
  currency = 'UGX',
}: {
  label: string
  value: number | null | undefined
  currency?: string | null
}) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{formatCurrency(value, currency || 'UGX')}</span>
    </div>
  )
}

function SummaryTile({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: typeof Package
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="break-words text-lg font-semibold">{value}</div>
      </CardContent>
    </Card>
  )
}

function EditConsignmentDialog({
  consignment,
  open,
  onOpenChange,
  onSaved,
}: {
  consignment: ConsignmentDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const [destinationAddress, setDestinationAddress] = useState(consignment.destination_address || consignment.destination_port || '')
  const [shippingMethod, setShippingMethod] = useState(consignment.shipping_method || 'AIR')
  const [serviceType, setServiceType] = useState<HelmServiceType>(consignment.service_type || 'END_TO_END_IMPORT')
  const [inquiryStage, setInquiryStage] = useState<HelmInquiryStage>(consignment.inquiry_stage || 'NEW_INQUIRY')
  const [ownerUserId, setOwnerUserId] = useState(consignment.owner_user_id || '')
  const [nextAction, setNextAction] = useState(consignment.next_action || '')
  const [communicationChannel, setCommunicationChannel] = useState<HelmCommunicationChannel>(
    consignment.preferred_communication_channel || 'EMAIL',
  )
  const [serviceNotes, setServiceNotes] = useState(consignment.service_notes || '')
  const [trackingNumber, setTrackingNumber] = useState(
    consignment.international_tracking_number
      || consignment.tracking_number
      || consignment.domestic_tracking_numbers?.[0]
      || '',
  )
  const [status, setStatus] = useState(consignment.status)
  const [visibility, setVisibility] = useState(consignment.visibility || 'NOT_READY')
  const [paymentStatus, setPaymentStatus] = useState(consignment.payment_status || 'PENDING')
  const [insuranceOpted, setInsuranceOpted] = useState(Boolean(consignment.insurance_opted ?? consignment.insurance))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)

    if (!destinationAddress.trim()) {
      setError('Destination address is required')
      setSubmitting(false)
      return
    }

    try {
      await apiClient.updateConsignment(consignment.id, {
        destination_address: destinationAddress.trim(),
        shipping_method: shippingMethod as UpdateConsignmentRequest['shipping_method'],
        service_type: serviceType,
        inquiry_stage: inquiryStage,
        owner_user_id: ownerUserId.trim() || null,
        next_action: nextAction.trim() || null,
        preferred_communication_channel: communicationChannel,
        service_notes: serviceNotes.trim() || null,
        tracking_number: trackingNumber.trim() || null,
        status,
        visibility: visibility as UpdateConsignmentRequest['visibility'],
        payment_status: paymentStatus as UpdateConsignmentRequest['payment_status'],
        insurance_opted: insuranceOpted,
      })
      onOpenChange(false)
      onSaved()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update consignment')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Consignment</DialogTitle>
          <DialogDescription>Update shipment workflow, tracking, and payment fields.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="destination-address">Destination</Label>
            <Textarea
              id="destination-address"
              value={destinationAddress}
              onChange={(event) => setDestinationAddress(event.target.value)}
              disabled={submitting}
              rows={3}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="service-type">Service Type</Label>
              <Select value={serviceType} onValueChange={(value) => setServiceType(value as HelmServiceType)} disabled={submitting}>
                <SelectTrigger id="service-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypeOptions.map((option) => (
                    <SelectItem key={option} value={option}>{formatLabel(option)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inquiry-stage">Inquiry Stage</Label>
              <Select value={inquiryStage} onValueChange={(value) => setInquiryStage(value as HelmInquiryStage)} disabled={submitting}>
                <SelectTrigger id="inquiry-stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {inquiryStageOptions.map((option) => (
                    <SelectItem key={option} value={option}>{formatLabel(option)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="communication-channel">Preferred Channel</Label>
              <Select
                value={communicationChannel}
                onValueChange={(value) => setCommunicationChannel(value as HelmCommunicationChannel)}
                disabled={submitting}
              >
                <SelectTrigger id="communication-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {communicationChannelOptions.map((option) => (
                    <SelectItem key={option} value={option}>{formatLabel(option)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="owner-user-id">Owner</Label>
              <Input
                id="owner-user-id"
                value={ownerUserId}
                onChange={(event) => setOwnerUserId(event.target.value)}
                disabled={submitting}
                placeholder="User UUID"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="next-action">Next Action</Label>
              <Input
                id="next-action"
                value={nextAction}
                onChange={(event) => setNextAction(event.target.value)}
                disabled={submitting}
                placeholder="Quote, chase invoice, confirm PVoC, clear goods..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shipping-method">Shipping Method</Label>
              <Select value={shippingMethod} onValueChange={setShippingMethod} disabled={submitting}>
                <SelectTrigger id="shipping-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AIR">Air</SelectItem>
                  <SelectItem value="SEA">Sea</SelectItem>
                  <SelectItem value="ROAD">Road</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tracking-number">Tracking Number</Label>
              <Input
                id="tracking-number"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
                disabled={submitting}
                placeholder="Container, waybill, or domestic tracking"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(value) => setStatus(value as Consignment['status'])} disabled={submitting}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option} value={option}>{formatLabel(option)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="visibility">Visibility</Label>
              <Select
                value={visibility}
                onValueChange={(value) => setVisibility(value as NonNullable<UpdateConsignmentRequest['visibility']>)}
                disabled={submitting}
              >
                <SelectTrigger id="visibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NOT_READY">Not Ready</SelectItem>
                  <SelectItem value="READY_FOR_SHIPMENT">Ready For Shipment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-status">Payment Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus} disabled={submitting}>
                <SelectTrigger id="payment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="INVOICED">Invoiced</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-3 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                checked={insuranceOpted}
                onChange={(event) => setInsuranceOpted(event.target.checked)}
                disabled={submitting}
                className="h-4 w-4"
              />
              Insurance opted
            </label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="service-notes">Service Notes</Label>
            <Textarea
              id="service-notes"
              value={serviceNotes}
              onChange={(event) => setServiceNotes(event.target.value)}
              disabled={submitting}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function buildCustomerUpdateMessage(consignment: ConsignmentDetail, customerName: string) {
  const tracking = displayTracking(consignment)
  const status = formatLabel(consignment.status)
  const nextAction = consignment.next_action ? `\nNext step: ${consignment.next_action}` : ''
  return [
    `Hi ${customerName},`,
    '',
    `Update on your Helm ${formatLabel(consignment.service_type).toLowerCase()} request: ${status}.`,
    tracking !== 'Not assigned' ? `Tracking/reference: ${tracking}.` : null,
    consignment.destination_address ? `Destination: ${consignment.destination_address}.` : null,
    nextAction.trim() || null,
    '',
    'Regards,',
    'Xtellar Helm',
  ].filter(Boolean).join('\n')
}

function CommunicationsPanel({
  consignment,
  communications,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  communications: HelmCommunication[]
  isAdmin: boolean
  onChanged: () => void
}) {
  const customerName = consignment.customer?.company_name || consignment.customer?.name || 'customer'
  const customerEmail = consignment.customer?.email || ''
  const whatsappPhone = normalizeWhatsAppPhone(consignment.customer?.phone)
  const defaultSubject = `Helm update for ${displayTracking(consignment) === 'Not assigned' ? consignment.id.slice(0, 8) : displayTracking(consignment)}`
  const defaultMessage = buildCustomerUpdateMessage(consignment, customerName)
  const [emailSubject, setEmailSubject] = useState(defaultSubject)
  const [emailBody, setEmailBody] = useState(defaultMessage)
  const [whatsAppBody, setWhatsAppBody] = useState(defaultMessage)
  const [submitting, setSubmitting] = useState<'EMAIL' | 'WHATSAPP' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleQueueEmail(event: FormEvent) {
    event.preventDefault()
    if (!customerEmail) {
      setError('Customer email is not set')
      return
    }

    setSubmitting('EMAIL')
    setError(null)
    try {
      await apiClient.createCommunication({
        customer_id: consignment.customer_id,
        consignment_id: consignment.id,
        channel: 'EMAIL',
        status: 'QUEUED',
        template_key: 'HELM_STATUS_UPDATE',
        recipient: customerEmail,
        subject: emailSubject.trim() || defaultSubject,
        body: emailBody.trim(),
        metadata: {
          status: consignment.status,
          inquiry_stage: consignment.inquiry_stage,
          service_type: consignment.service_type,
        },
      })
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to queue email update')
    } finally {
      setSubmitting(null)
    }
  }

  async function handleOpenWhatsApp() {
    if (!whatsappPhone) {
      setError('Customer phone is not set')
      return
    }

    setSubmitting('WHATSAPP')
    setError(null)
    const body = whatsAppBody.trim() || defaultMessage
    try {
      await apiClient.createCommunication({
        customer_id: consignment.customer_id,
        consignment_id: consignment.id,
        channel: 'WHATSAPP',
        status: 'LOGGED',
        template_key: 'HELM_WHATSAPP_STATUS_UPDATE',
        recipient: `+${whatsappPhone}`,
        body,
        metadata: {
          status: consignment.status,
          inquiry_stage: consignment.inquiry_stage,
          service_type: consignment.service_type,
        },
      })
      window.open(`https://wa.me/${whatsappPhone}?text=${encodeURIComponent(body)}`, '_blank', 'noopener,noreferrer')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to log WhatsApp contact')
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Communications</CardTitle>
        <CardDescription>Email queue and WhatsApp contact history for this inquiry.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Preferred Channel</Label>
            <p className="mt-1 font-medium">{formatLabel(consignment.preferred_communication_channel)}</p>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Last Communication</Label>
            <p className="mt-1 font-medium">{formatDateTime(consignment.last_communication_at)}</p>
          </div>
          <div>
            <Label className="text-xs uppercase text-muted-foreground">Next Action</Label>
            <p className="mt-1 font-medium">{consignment.next_action || 'Not set'}</p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isAdmin ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <form onSubmit={handleQueueEmail} className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">Email Update</div>
                  <div className="text-sm text-muted-foreground">{customerEmail || 'No email on customer'}</div>
                </div>
                <Button type="submit" size="sm" disabled={submitting !== null || !customerEmail}>
                  {submitting === 'EMAIL' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Queue
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-subject">Subject</Label>
                <Input
                  id="email-subject"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                  disabled={submitting !== null}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-body">Message</Label>
                <Textarea
                  id="email-body"
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={7}
                />
              </div>
            </form>

            <div className="space-y-3 rounded-md border p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">WhatsApp</div>
                  <div className="text-sm text-muted-foreground">{consignment.customer?.phone || 'No phone on customer'}</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={handleOpenWhatsApp} disabled={submitting !== null || !whatsappPhone}>
                  {submitting === 'WHATSAPP' ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                  Open
                </Button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp-body">Message</Label>
                <Textarea
                  id="whatsapp-body"
                  value={whatsAppBody}
                  onChange={(event) => setWhatsAppBody(event.target.value)}
                  disabled={submitting !== null}
                  rows={9}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            Admin access is required to queue or log customer communications.
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">History</h3>
            <Badge variant="outline">{communications.length}</Badge>
          </div>
          {communications.length === 0 ? (
            <p className="rounded-md border p-4 text-sm text-muted-foreground">No communication events have been recorded for this inquiry.</p>
          ) : (
            <div className="divide-y rounded-md border">
              {communications.map((communication) => (
                <div key={communication.id} className="space-y-2 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={communication.channel === 'WHATSAPP' ? 'secondary' : 'outline'}>
                      {formatLabel(communication.channel)}
                    </Badge>
                    <Badge variant="outline">{formatLabel(communication.status)}</Badge>
                    <span className="text-sm text-muted-foreground">{formatDateTime(communication.sent_at || communication.created_at)}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">{communication.recipient}</div>
                  {communication.subject && <div className="font-medium">{communication.subject}</div>}
                  <p className="whitespace-pre-wrap text-sm">{communication.body}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {consignment.service_notes && (
          <div className="rounded-md border p-4">
            <Label className="text-xs uppercase text-muted-foreground">Service Notes</Label>
            <p className="mt-2 whitespace-pre-wrap text-sm">{consignment.service_notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const documentTypeOptions: ConsignmentDocumentType[] = [
  'COMMERCIAL_INVOICE',
  'RECEIPT',
  'PACKING_LIST',
  'DELIVERY_NOTE',
  'PROOF_OF_PAYMENT',
  'BILL_OF_LADING',
  'AIRWAY_BILL',
  'CUSTOMS_DECLARATION',
  'ORIGINAL_INVOICE',
  'GOX_SHIPPING_INVOICE',
]

const consignmentTabValues = [
  'items',
  'tax',
  'documents',
  'tracking',
  'communications',
  'customer',
] as const

type ConsignmentTabValue = (typeof consignmentTabValues)[number]

function isConsignmentTabValue(value: string | undefined): value is ConsignmentTabValue {
  return consignmentTabValues.includes(value as ConsignmentTabValue)
}

function EditableItemsPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [items, setItems] = useState<ConsignmentItem[]>(consignment.items)
  const [savingItemId, setSavingItemId] = useState<string | null>(null)
  const [classifying, setClassifying] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  function patchItem(itemId: string, patch: Partial<ConsignmentItem>) {
    setItems((current) => current.map((item) => (item.id === itemId ? { ...item, ...patch } : item)))
  }

  async function saveItem(item: ConsignmentItem) {
    setSavingItemId(item.id)
    setError(null)
    setMessage(null)
    try {
      const updated = await apiClient.updateConsignmentItem(consignment.id, item.id, {
        description: item.description || item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        product_use: item.product_use || null,
        material: item.material || null,
        hs_code_china: item.hs_code_china || null,
        hs_code_uganda: item.hs_code_uganda || null,
        tracking_number: item.tracking_number || null,
        is_special_item: Boolean(item.is_special_item),
        special_item_type: item.special_item_type || null,
      })
      patchItem(item.id, updated)
      setMessage(`Saved ${updated.name}`)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save item')
    } finally {
      setSavingItemId(null)
    }
  }

  async function classifyMissing() {
    const targets = items.filter((item) => (item.description || item.name) && !item.hs_code_uganda)
    if (targets.length === 0) {
      setMessage('All items already have Uganda HS codes.')
      return
    }

    setClassifying(true)
    setError(null)
    setMessage(null)
    let classified = 0
    try {
      for (const item of targets) {
        const result = await apiClient.classify({ description: item.description || item.name })
        const updated = await apiClient.updateConsignmentItem(consignment.id, item.id, {
          hs_code_uganda: result.hs_code,
          ai_confidence: result.confidence,
        })
        patchItem(item.id, updated)
        classified += 1
      }
      setMessage(`Classified ${classified} item${classified === 1 ? '' : 's'}.`)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to classify items')
    } finally {
      setClassifying(false)
    }
  }

  const totalValue = items.reduce((sum, item) => sum + (item.total_value ?? item.quantity * item.unit_price), 0)

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Consignment Items</CardTitle>
            <CardDescription>
              {items.length} line items, {formatCurrency(totalValue)}. Edit quantities, prices, HS codes, tracking, use, and material inline.
            </CardDescription>
          </div>
          {isAdmin && (
            <Button type="button" variant="outline" size="sm" onClick={classifyMissing} disabled={classifying || items.length === 0}>
              {classifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Classify Missing
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {items.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">No items have been recorded for this consignment.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[260px]">Item</TableHead>
                  <TableHead className="min-w-[90px] text-right">Qty</TableHead>
                  <TableHead className="min-w-[120px] text-right">Unit</TableHead>
                  <TableHead className="min-w-[120px] text-right">Total</TableHead>
                  <TableHead className="min-w-[140px]">HS Uganda</TableHead>
                  <TableHead className="min-w-[140px]">HS China</TableHead>
                  <TableHead className="min-w-[160px]">Tracking</TableHead>
                  <TableHead className="min-w-[180px]">Use</TableHead>
                  <TableHead className="min-w-[180px]">Material</TableHead>
                  <TableHead className="min-w-[110px]">Special</TableHead>
                  {isAdmin && <TableHead className="w-[90px]" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {isAdmin ? (
                        <Textarea
                          aria-label={`Description for ${item.name}`}
                          value={item.description || item.name}
                          onChange={(event) => patchItem(item.id, { description: event.target.value })}
                          rows={2}
                        />
                      ) : (
                        <>
                          <div className="font-medium">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.description}</div>
                        </>
                      )}
                      {item.simple_name && <div className="mt-1 text-xs text-muted-foreground">{item.simple_name}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin ? (
                        <Input
                          aria-label={`Quantity for ${item.name}`}
                          className="min-w-20 text-right"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(event) => patchItem(item.id, { quantity: Number(event.target.value) || 1 })}
                        />
                      ) : item.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin ? (
                        <Input
                          aria-label={`Unit price for ${item.name}`}
                          className="min-w-28 text-right"
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(event) => patchItem(item.id, { unit_price: Number(event.target.value) || 0 })}
                        />
                      ) : formatCurrency(item.unit_price)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Input
                          aria-label={`Uganda HS code for ${item.name}`}
                          value={item.hs_code_uganda || ''}
                          onChange={(event) => patchItem(item.id, { hs_code_uganda: event.target.value })}
                        />
                      ) : item.hs_code_uganda || item.hs_code || 'Pending'}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Input
                          aria-label={`China HS code for ${item.name}`}
                          value={item.hs_code_china || ''}
                          onChange={(event) => patchItem(item.id, { hs_code_china: event.target.value })}
                        />
                      ) : item.hs_code_china || 'Not set'}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Input
                          aria-label={`Tracking number for ${item.name}`}
                          value={item.tracking_number || ''}
                          onChange={(event) => patchItem(item.id, { tracking_number: event.target.value })}
                        />
                      ) : item.tracking_number || 'Not set'}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Input
                          aria-label={`Product use for ${item.name}`}
                          value={item.product_use || ''}
                          onChange={(event) => patchItem(item.id, { product_use: event.target.value })}
                        />
                      ) : item.product_use || 'Not set'}
                    </TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Input
                          aria-label={`Material for ${item.name}`}
                          value={item.material || ''}
                          onChange={(event) => patchItem(item.id, { material: event.target.value })}
                        />
                      ) : item.material || 'Not set'}
                    </TableCell>
                    <TableCell>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(item.is_special_item)}
                          disabled={!isAdmin}
                          onChange={(event) => patchItem(item.id, { is_special_item: event.target.checked })}
                        />
                        Special
                      </label>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Button type="button" size="sm" onClick={() => saveItem(item)} disabled={savingItemId === item.id}>
                          {savingItemId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          Save
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TaxOperationsPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [recalculating, setRecalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const taxEstimate = consignment.tax_estimate ?? null

  async function handleRecalculate() {
    setRecalculating(true)
    setError(null)
    setMessage(null)
    try {
      const result = await apiClient.recalculateConsignmentTaxes(consignment.id)
      setMessage(`Recalculated ${result.items.length} item tax lines. Total tax: ${formatCurrency(result.totals.total_tax, 'UGX')}`)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to recalculate taxes')
    } finally {
      setRecalculating(false)
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>Item Tax Lines</CardTitle>
              <CardDescription>Duty, VAT, withholding, infrastructure levy, and excise values per item.</CardDescription>
            </div>
            {isAdmin && (
              <Button type="button" variant="outline" size="sm" onClick={handleRecalculate} disabled={recalculating}>
                {recalculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                Estimate Tax
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Duty</TableHead>
                <TableHead className="text-right">VAT</TableHead>
                <TableHead className="text-right">WHT</TableHead>
                <TableHead className="text-right">Infra</TableHead>
                <TableHead className="text-right">Excise</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consignment.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.hs_code_uganda || item.hs_code || 'Missing HS code'}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(item.import_duty, 'UGX')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.vat, 'UGX')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.withholding_tax, 'UGX')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.infra_levy, 'UGX')}</TableCell>
                  <TableCell className="text-right">{formatCurrency(item.excise_duty, 'UGX')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ReceiptText className="h-4 w-4" />
            Estimate Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {taxEstimate ? (
            <>
              <div>
                <div className="text-sm text-muted-foreground">Total Tax</div>
                <div className="text-2xl font-semibold">{formatCurrency(taxEstimate.total_tax, taxEstimate.target_currency || 'UGX')}</div>
              </div>
              <Separator />
              <TaxAmountRow label="CIF Value" value={taxEstimate.cif_value} currency={taxEstimate.currency} />
              <TaxAmountRow label="Import Duty" value={taxEstimate.import_duty} currency={taxEstimate.target_currency} />
              <TaxAmountRow label="VAT" value={taxEstimate.vat} currency={taxEstimate.target_currency} />
              <TaxAmountRow label="Withholding" value={taxEstimate.withholding_tax} currency={taxEstimate.target_currency} />
              <TaxAmountRow label="Infrastructure Levy" value={taxEstimate.infra_levy} currency={taxEstimate.target_currency} />
              <TaxAmountRow label="Excise Duty" value={taxEstimate.excise_duty} currency={taxEstimate.target_currency} />
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No tax estimate has been calculated for this consignment.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function documentHref(document: ConsignmentDocument) {
  if (/^https?:\/\//.test(document.file_url)) return document.file_url
  return apiClient.getDocumentDownloadUrl(document.id)
}

function DocumentsPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [documentType, setDocumentType] = useState<ConsignmentDocumentType>('COMMERCIAL_INVOICE')
  const [uploadType, setUploadType] = useState<ConsignmentDocumentType>('ORIGINAL_INVOICE')
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [generateOpen, setGenerateOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [generateDescription, setGenerateDescription] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadReference, setUploadReference] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  async function generateDocument() {
    setBusy('generate')
    setError(null)
    setMessage(null)
    try {
      const document = await apiClient.generateConsignmentDocument(consignment.id, {
        type: documentType,
        description: generateDescription.trim() || null,
      })
      setMessage(`Generated ${formatLabel(document.type)}.`)
      setGenerateDescription('')
      setGenerateOpen(false)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to generate document')
    } finally {
      setBusy(null)
    }
  }

  async function uploadDocument(event: FormEvent) {
    event.preventDefault()
    if (!uploadFile) {
      setError('Choose a file to upload')
      return
    }

    setBusy('upload')
    setError(null)
    setMessage(null)
    try {
      const upload = await apiClient.createStorageUpload({
        kind: 'invoice',
        file_name: uploadFile.name,
        content_type: uploadFile.type || 'application/octet-stream',
        size: uploadFile.size,
      })
      await apiClient.uploadToSignedUrl(upload, uploadFile)
      await apiClient.attachConsignmentDocument(consignment.id, {
        type: uploadType,
        file_url: upload.public_url,
        file_name: uploadFile.name,
        file_size_bytes: uploadFile.size,
        generated_by: 'CUSTOMER_UPLOAD',
        reference_number: uploadReference.trim() || null,
        description: uploadDescription.trim() || null,
        content_type: uploadFile.type || 'application/octet-stream',
      })
      setUploadFile(null)
      setUploadDescription('')
      setUploadReference('')
      setUploadOpen(false)
      setMessage(`Uploaded ${uploadFile.name}.`)
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to upload document')
    } finally {
      setBusy(null)
    }
  }

  async function deleteDocument(id: string) {
    setBusy(id)
    setError(null)
    setMessage(null)
    try {
      await apiClient.deleteConsignmentDocument(id)
      setMessage('Document archived.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to archive document')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Generated and Uploaded Documents</CardTitle>
            <CardDescription>Invoices, packing lists, receipts, customs paperwork, and shipping documents.</CardDescription>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setGenerateOpen(true)} disabled={busy !== null}>
                <FilePlus2 className="h-4 w-4" />
                Generate PDF
              </Button>
              <Button type="button" onClick={() => setUploadOpen(true)} disabled={busy !== null}>
                <Upload className="h-4 w-4" />
                Upload Artifact
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {consignment.documents.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">No documents have been generated or uploaded yet.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {consignment.documents.map((document) => (
              <div key={document.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{document.file_name || formatLabel(document.type)}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span>{formatLabel(document.type)}</span>
                    <span>{formatLabel(document.generated_by)}</span>
                    <span>v{document.version ?? 1}</span>
                    <span>{formatLabel(document.status || 'ACTIVE')}</span>
                    <span>{formatDate(document.created_at)}</span>
                  </div>
                  {document.description && (
                    <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{document.description}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={documentHref(document)} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Open
                    </a>
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="destructive"
                      size="sm"
                      type="button"
                      onClick={() => deleteDocument(document.id)}
                      disabled={busy === document.id}
                    >
                      {busy === document.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                      Archive
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Document</DialogTitle>
            <DialogDescription>Create a governed PDF record for this consignment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="generate-document-type">Document Type</Label>
              <Select value={documentType} onValueChange={(value) => setDocumentType(value as ConsignmentDocumentType)}>
                <SelectTrigger id="generate-document-type" aria-label="Document type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypeOptions.map((type) => (
                    <SelectItem key={type} value={type}>{formatLabel(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="generate-document-description">Description</Label>
              <Textarea
                id="generate-document-description"
                value={generateDescription}
                onChange={(event) => setGenerateDescription(event.target.value)}
                placeholder="What this document is for, what changed, or who requested it."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setGenerateOpen(false)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button type="button" onClick={generateDocument} disabled={busy !== null}>
              {busy === 'generate' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FilePlus2 className="h-4 w-4" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Artifact</DialogTitle>
            <DialogDescription>Attach a customer, supplier, forwarder, or compliance file to this consignment.</DialogDescription>
          </DialogHeader>
          <form id="upload-consignment-document-form" onSubmit={uploadDocument} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="upload-document-type">Document Type</Label>
              <Select value={uploadType} onValueChange={(value) => setUploadType(value as ConsignmentDocumentType)}>
                <SelectTrigger id="upload-document-type" aria-label="Upload document type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypeOptions.map((type) => (
                    <SelectItem key={type} value={type}>{formatLabel(type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="upload-document-reference">Reference Number</Label>
              <Input
                id="upload-document-reference"
                value={uploadReference}
                onChange={(event) => setUploadReference(event.target.value)}
                placeholder="Invoice, BL, AWB, receipt, or supplier reference"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="upload-document-description">Description</Label>
              <Textarea
                id="upload-document-description"
                value={uploadDescription}
                onChange={(event) => setUploadDescription(event.target.value)}
                placeholder="Describe the artifact and how staff should use it."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="upload-document-file">File</Label>
              <Input
                id="upload-document-file"
                aria-label="Document file"
                type="file"
                onChange={(event) => setUploadFile(event.target.files?.[0] ?? null)}
              />
            </div>
          </form>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} disabled={busy !== null}>
              Cancel
            </Button>
            <Button type="submit" form="upload-consignment-document-form" disabled={busy !== null || !uploadFile}>
              {busy === 'upload' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function SeaTrackingLookupCard({
  consignment,
  isAdmin,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
}) {
  const reference = getConsignmentTrackingReference(consignment)
  const shouldLookup = shouldLoadSeaTracking(consignment)
  const [registering, setRegistering] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)
  const [registrationMessage, setRegistrationMessage] = useState<string | null>(null)

  const { data, error, loading, reload } = useAsyncData(async () => {
    if (!reference || !shouldLookup) return null
    const [shipment, containerPage] = await Promise.all([
      apiClient.getSeaTrackingShipment(reference),
      apiClient.listSeaTrackingContainers(reference, 1, 25),
    ])

    return {
      shipment,
      containers: containerPage.containers,
    }
  }, [consignment.id, reference, shouldLookup])

  if (!reference || !shouldLookup) return null

  const shipment = data?.shipment ?? null
  const containers = data?.containers ?? []
  const containerCount = getShipmentContainerCount(shipment, containers)
  const firstContainer = containers[0]
  const dashboardUrl = shipment?.dashboard_url

  async function registerSeaTracking() {
    if (!reference) return
    setRegistering(true)
    setRegistrationError(null)
    setRegistrationMessage(null)
    try {
      const customerName = consignment.customer?.company_name || consignment.customer?.name || 'Helm customer'
      await apiClient.importSeaTrackingShipment({
        reference,
        tracking_type: 'container',
        tracking_number: reference,
        name: `${customerName} / ${reference}`,
        tags: ['helm', 'sea-tracking'],
        custom_fields: {
          consignment_id: consignment.id,
        },
        destination_country_code: 'UG',
      })
      setRegistrationMessage('Visiwise import accepted.')
      reload()
    } catch (error) {
      setRegistrationError(error instanceof Error ? error.message : 'Failed to register sea tracking')
    } finally {
      setRegistering(false)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle>Visiwise Sea Tracking</CardTitle>
          <CardDescription>Container reference {reference}</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dashboardUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={dashboardUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
                Open Visiwise
              </a>
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={reload} disabled={loading}>
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {registrationError && (
          <Alert variant="destructive">
            <AlertDescription>{registrationError}</AlertDescription>
          </Alert>
        )}
        {registrationMessage && (
          <Alert>
            <AlertDescription>{registrationMessage}</AlertDescription>
          </Alert>
        )}
        {loading && (
          <div className="flex items-center gap-2 rounded-md border p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading Visiwise tracking...
          </div>
        )}
        {error && (
          <div className="space-y-3">
            <Alert variant="destructive">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
            {isAdmin && (
              <Button type="button" variant="outline" onClick={registerSeaTracking} disabled={registering}>
                {registering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ship className="h-4 w-4" />}
                Register in Visiwise
              </Button>
            )}
          </div>
        )}
        {!loading && !error && shipment && (
          <>
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Provider Status</Label>
                <div className="mt-2">
                  <Badge variant="secondary">{formatLabel(shipment.process_status || shipment.data_status)}</Badge>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Data Freshness</Label>
                <p className="mt-2 font-medium">{formatLabel(shipment.data_freshness_status)}</p>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Containers</Label>
                <p className="mt-2 font-medium">{containerCount.toLocaleString()}</p>
              </div>
              <div className="rounded-md border p-3">
                <Label className="text-xs uppercase text-muted-foreground">Last Sync</Label>
                <p className="mt-2 font-medium">{formatDateTime(shipment.last_successful_tracking_at || shipment.tracking_updated_at)}</p>
              </div>
            </div>

            {shipment.process_failure_reason && (
              <Alert variant="destructive">
                <AlertDescription>{shipment.process_failure_reason}</AlertDescription>
              </Alert>
            )}

            <div className="rounded-md border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Last Movement</Label>
                  <p className="mt-1 font-medium">{describeContainerMovement(firstContainer)}</p>
                </div>
                {firstContainer?.pod_terminal_name && (
                  <Badge variant="outline">{firstContainer.pod_terminal_name}</Badge>
                )}
              </div>
            </div>

            {containers.length > 0 ? (
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Container</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Movement</TableHead>
                      <TableHead>POD</TableHead>
                      <TableHead>Vessel</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {containers.map((container, index) => (
                      <TableRow key={container.container_number || `${reference}-${index}`}>
                        <TableCell className="font-medium">{container.container_number || reference}</TableCell>
                        <TableCell>{formatLabel(container.process_status)}</TableCell>
                        <TableCell>{describeContainerMovement(container)}</TableCell>
                        <TableCell>{container.pod_terminal_name || readString(container.pod, 'name') || 'Not set'}</TableCell>
                        <TableCell>{[container.arriving_vessel_name, container.arriving_vessel_voyage_no].filter(Boolean).join(' ') || 'Not set'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="rounded-md border p-4 text-sm text-muted-foreground">
                Visiwise returned the shipment but no container rows yet.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function TrackingPanel({
  consignment,
  isAdmin,
  onChanged,
}: {
  consignment: ConsignmentDetail
  isAdmin: boolean
  onChanged: () => void
}) {
  const [trackingNumber, setTrackingNumber] = useState(displayTracking(consignment) === 'Not assigned' ? '' : displayTracking(consignment))
  const [eventType, setEventType] = useState('STATUS_UPDATE')
  const [location, setLocation] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  async function recordTrackingEvent(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    setMessage(null)
    try {
      await apiClient.recordConsignmentTrackingEvent(consignment.id, {
        tracking_number: trackingNumber.trim() || null,
        event_type: eventType.trim(),
        location: location.trim() || null,
        description: description.trim() || null,
        occurred_at: new Date().toISOString(),
      })
      setDescription('')
      setMessage('Tracking update recorded.')
      onChanged()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to record tracking update')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <SeaTrackingLookupCard consignment={consignment} isAdmin={isAdmin} />

      <Card>
      <CardHeader>
        <CardTitle>Tracking Timeline</CardTitle>
        <CardDescription>Container, international, and domestic tracking events.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}

        {isAdmin && (
          <form onSubmit={recordTrackingEvent} className="grid gap-3 rounded-md border p-4 lg:grid-cols-[1fr_1fr_1fr_auto]">
            <div className="space-y-2">
              <Label htmlFor="tracking-reference">Tracking Reference</Label>
              <Input
                id="tracking-reference"
                value={trackingNumber}
                onChange={(event) => setTrackingNumber(event.target.value)}
                placeholder="Container, B/L, waybill, domestic tracking"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tracking-event">Event</Label>
              <Input id="tracking-event" value={eventType} onChange={(event) => setEventType(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tracking-location">Location</Label>
              <Input id="tracking-location" value={location} onChange={(event) => setLocation(event.target.value)} />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={submitting || !eventType.trim()}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Record
              </Button>
            </div>
            <div className="space-y-2 lg:col-span-4">
              <Label htmlFor="tracking-description">Description</Label>
              <Textarea
                id="tracking-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                rows={3}
                placeholder="Container discharged, customs exam booked, parcel arrived at warehouse..."
              />
            </div>
          </form>
        )}

        {consignment.tracking_events.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">No tracking events have been synced for this consignment.</p>
        ) : (
          <div className="space-y-4">
            {consignment.tracking_events.map((event) => (
              <div key={event.id} className="grid gap-1 border-l-2 border-muted pl-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{formatLabel(event.source)}</Badge>
                  <span className="font-medium">{formatLabel(event.event_type)}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatDate(event.occurred_at)} {event.location ? `at ${event.location}` : ''}
                </div>
                {event.description && <p className="text-sm">{event.description}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      </Card>
    </div>
  )
}

export function ConsignmentDetailPage() {
  const { id, tab } = useParams()
  const navigate = useNavigate()
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const activeTab = isConsignmentTabValue(tab) ? tab : 'items'

  const { data, error, loading, reload } = useAsyncData(async () => {
    if (!id) throw new Error('Consignment id is missing')
    const [consignment, profile, communications] = await Promise.all([
      apiClient.getConsignment(id),
      apiClient.getProfile().catch(() => null),
      apiClient.listCommunications({ consignment_id: id, limit: 50 }).catch(() => []),
    ])
    return { consignment, profile, communications }
  }, [id])

  const consignment = data?.consignment
  const communications = data?.communications ?? []
  const isAdmin = data?.profile?.is_admin || data?.profile?.role === 'admin'
  const customerName = consignment?.customer?.company_name || consignment?.customer?.name || 'Customer not linked'

  async function handleDelete() {
    if (!consignment) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await apiClient.deleteConsignment(consignment.id)
      navigate('/consignments')
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete consignment')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) return <LoadingState label="Loading consignment..." />
  if (error) return <ErrorState error={error} onRetry={reload} />
  if (!consignment) return <ErrorState error="Consignment not found" />

  const headerTitle = displayTracking(consignment) === 'Not assigned'
    ? `Consignment ${consignment.id.slice(0, 8)}`
    : displayTracking(consignment)

  function handleTabChange(value: string) {
    if (!id || !isConsignmentTabValue(value)) return
    navigate(value === 'items' ? `/consignments/${id}` : `/consignments/${id}/${value}`)
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={headerTitle}
        description={`${customerName} - ${formatLabel(consignment.status)}`}
        breadcrumbs={[
          { label: 'Consignments', href: '/consignments' },
          { label: headerTitle },
        ]}
        actions={(
          <>
            <Button variant="outline" size="sm" asChild>
              <Link to="/consignments">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </Button>
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
          </>
        )}
      />

      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(consignment.status)}>{formatLabel(consignment.status)}</Badge>
          <Badge variant="secondary">{formatLabel(consignment.service_type)}</Badge>
          <Badge variant="outline">{formatLabel(consignment.inquiry_stage)}</Badge>
          <Badge variant="outline">{formatLabel(consignment.shipping_method || consignment.mode)}</Badge>
          <Badge variant="outline">{formatLabel(consignment.visibility)}</Badge>
          <Badge variant="secondary">{formatLabel(consignment.payment_status)}</Badge>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <SummaryTile label="Customer" value={customerName} icon={Package} />
          <SummaryTile label="Destination" value={consignment.destination_address || consignment.destination_port} icon={MapPin} />
          <SummaryTile label="Tracking" value={displayTracking(consignment)} icon={consignment.shipping_method === 'SEA' ? Ship : Truck} />
          <SummaryTile label="Created" value={formatDate(consignment.created_at)} icon={Calendar} />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Workflow Context</CardTitle>
            <CardDescription>Service, owner, next action, and customer communication state.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Service</Label>
              <p className="mt-1 font-medium">{formatLabel(consignment.service_type)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Stage</Label>
              <p className="mt-1 font-medium">{formatLabel(consignment.inquiry_stage)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Owner</Label>
              <p className="mt-1 font-mono text-sm">{consignment.owner_user_id || 'Unassigned'}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Preferred Channel</Label>
              <p className="mt-1 font-medium">{formatLabel(consignment.preferred_communication_channel)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Last Communication</Label>
              <p className="mt-1 font-medium">{formatDateTime(consignment.last_communication_at)}</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Next Action</Label>
              <p className="mt-1 font-medium">{consignment.next_action || 'Not set'}</p>
            </div>
            {consignment.service_notes && (
              <div className="md:col-span-3">
                <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
                <p className="mt-1 whitespace-pre-wrap text-sm">{consignment.service_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="flex h-auto w-full flex-wrap justify-start">
            <TabsTrigger value="items">Items</TabsTrigger>
            <TabsTrigger value="tax">Tax Breakdown</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="tracking">Tracking</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="customer">Customer</TabsTrigger>
          </TabsList>

          <TabsContent value="items">
            <EditableItemsPanel
              key={`${consignment.id}-items`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="tax">
            <TaxOperationsPanel
              key={`${consignment.id}-tax`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsPanel
              key={`${consignment.id}-documents`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="tracking">
            <TrackingPanel
              key={`${consignment.id}-tracking`}
              consignment={consignment}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="communications">
            <CommunicationsPanel
              key={consignment.id}
              consignment={consignment}
              communications={communications}
              isAdmin={Boolean(isAdmin)}
              onChanged={reload}
            />
          </TabsContent>

          <TabsContent value="customer">
            <Card>
              <CardHeader>
                <CardTitle>{customerName}</CardTitle>
                <CardDescription>Customer contact and consignment ownership.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Email</Label>
                  <p className="mt-1">{consignment.customer?.email || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Phone</Label>
                  <p className="mt-1">{consignment.customer?.phone || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">TIN</Label>
                  <p className="mt-1 font-mono">{consignment.customer?.tin_number || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">WhatsApp</Label>
                  <p className="mt-1 font-mono">{consignment.customer?.whatsapp_group_id || 'Not set'}</p>
                </div>
                {consignment.customer_id && (
                  <div className="md:col-span-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/customers/${consignment.customer_id}`}>Open Customer</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <EditConsignmentDialog
        consignment={consignment}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={reload}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Consignment</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the consignment and its linked items, tax estimate, documents, and tracking records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
